/**
 * Unified centered alert / confirmation dialog system.
 *
 * Replaces every native window.alert / confirm / prompt across the app with
 * a promise-based imperative API:
 *
 *   const ok = await confirm({ title, description, variant, confirmLabel });
 *   const pw = await promptPassword({ title, description });
 *   await alert({ title, description, variant: 'error' });
 *
 * Wrap the app once with <ConfirmDialogProvider /> (already mounted in App.js).
 * Accessible (focus trapped by shadcn AlertDialog primitive, ESC closes, keyboard
 * navigation), centered, matches WingWise dark aesthetic.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Loader2, AlertTriangle, CheckCircle2, Info, ShieldAlert, Trash2, ShieldCheck, XCircle, Zap } from "lucide-react";

const Ctx = createContext(null);

const VARIANT_STYLE = {
  info:        { icon: Info,          tone: "text-[#0A84FF]",       ring: "border-[#0A84FF]/40",       confirm: "btn-primary" },
  success:     { icon: CheckCircle2,  tone: "pnl-up",                ring: "border-[#30D158]/40",      confirm: "btn-primary" },
  warning:     { icon: AlertTriangle, tone: "text-signal-yellow",    ring: "border-signal-yellow/40",  confirm: "btn-primary" },
  error:       { icon: XCircle,       tone: "pnl-down",              ring: "border-signal-red/40",     confirm: "btn-primary" },
  confirm:     { icon: ShieldCheck,   tone: "text-foreground",       ring: "border-border/60",         confirm: "btn-primary" },
  destructive: { icon: Trash2,        tone: "pnl-down",              ring: "border-signal-red/50",     confirm: "btn-danger" },
  risk:        { icon: ShieldAlert,   tone: "pnl-down",              ring: "border-signal-red/50",     confirm: "btn-primary" },
  broker:      { icon: AlertTriangle, tone: "text-signal-yellow",    ring: "border-signal-yellow/40",  confirm: "btn-primary" },
  budget:      { icon: Zap,           tone: "text-signal-yellow",    ring: "border-signal-yellow/40",  confirm: "btn-primary" },
  unsaved:     { icon: AlertTriangle, tone: "text-signal-yellow",    ring: "border-signal-yellow/40",  confirm: "btn-primary" },
  kill:        { icon: ShieldAlert,   tone: "pnl-down",              ring: "border-signal-red/50",     confirm: "btn-danger" },
};

export function ConfirmDialogProvider({ children }) {
  const [stack, setStack] = useState([]);
  const idRef = useRef(0);

  const push = useCallback((opts) => new Promise((resolve) => {
    // Dedupe: if a dialog with the same singleton key is already open, resolve to it
    if (opts.singletonKey) {
      const existing = stackRef.current.find((d) => d.singletonKey === opts.singletonKey);
      if (existing) {
        existing.piggyback.push(resolve);
        return;
      }
    }
    idRef.current += 1;
    const id = idRef.current;
    const piggyback = [];
    setStack((s) => [...s, { id, resolve, piggyback, ...opts }]);
  }), []);

  const stackRef = useRef([]);
  useEffect(() => { stackRef.current = stack; }, [stack]);

  const close = useCallback((id, result) => {
    setStack((s) => {
      const dlg = s.find((x) => x.id === id);
      if (dlg) {
        dlg.resolve(result);
        // Propagate the same result to piggybacked promises
        (dlg.piggyback || []).forEach((r) => r(result));
      }
      return s.filter((x) => x.id !== id);
    });
  }, []);

  const api = {
    confirm: (o) => push({ mode: "confirm", ...o }),
    alert:   (o) => push({ mode: "alert", ...o }),
    prompt:  (o) => push({ mode: "prompt", ...o }),
    promptPassword: (o) => push({ mode: "prompt", kind: "password", ...o }),
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      {stack.map((dlg) => <DialogInstance key={dlg.id} dlg={dlg} close={close} />)}
    </Ctx.Provider>
  );
}

function DialogInstance({ dlg, close }) {
  const {
    id, mode, kind, title, description, details, variant = "confirm",
    confirmLabel, cancelLabel = "Cancel", placeholder, defaultValue = "",
    required = true, testId,
  } = dlg;
  const style = VARIANT_STYLE[variant] || VARIANT_STYLE.confirm;
  const Icon = style.icon;
  const [value, setValue] = useState(defaultValue);
  const [busy, setBusy] = useState(false);
  const isPrompt = mode === "prompt";

  const finalConfirm = confirmLabel || (
    mode === "alert" ? "OK" :
    variant === "destructive" ? "Delete" :
    variant === "kill" ? "Confirm" :
    isPrompt ? "Submit" : "Confirm"
  );

  const onConfirm = async () => {
    if (isPrompt && required && !value.trim()) return;
    setBusy(true);
    try {
      if (mode === "alert") close(id, true);
      else if (isPrompt) close(id, value);
      else close(id, true);
    } finally { setBusy(false); }
  };
  const onCancel = () => close(id, isPrompt ? null : false);
  // ESC / backdrop-close → resolve with `null` so callers can distinguish it from
  // an explicit Cancel button click (which resolves with `false`).
  const onDismiss = () => close(id, null);

  return (
    <AlertDialog open onOpenChange={(v) => !v && onDismiss()}>
      <AlertDialogContent
        className={`!rounded-none border ${style.ring} max-w-md bg-card`}
        data-testid={testId || `confirm-dialog-${variant}`}
      >
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div className={`shrink-0 mt-0.5 ${style.tone}`}>
              <Icon size={20} strokeWidth={1.5} />
            </div>
            <div className="flex-1 space-y-1">
              <AlertDialogTitle className="mono uppercase tracking-wider text-sm">{title}</AlertDialogTitle>
              {description && <AlertDialogDescription className="text-xs text-muted-foreground">{description}</AlertDialogDescription>}
            </div>
          </div>
        </AlertDialogHeader>

        {details && (
          <div className="text-xs border border-border/40 bg-background/20 p-3 space-y-1 mono">
            {typeof details === "string"
              ? <div>{details}</div>
              : Object.entries(details).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <span className="text-muted-foreground uppercase tracking-widest text-[10px]">{k}</span>
                    <span>{String(v)}</span>
                  </div>
                ))}
          </div>
        )}

        {isPrompt && (
          <Input
            autoFocus
            type={kind === "password" ? "password" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder || ""}
            className="!rounded-none mono"
            data-testid="confirm-dialog-input"
            onKeyDown={(e) => { if (e.key === "Enter") onConfirm(); }}
          />
        )}

        <AlertDialogFooter>
          {mode !== "alert" && (
            <AlertDialogCancel
              onClick={onCancel}
              className="!rounded-none btn-outline"
              data-testid="confirm-dialog-cancel"
            >
              {cancelLabel}
            </AlertDialogCancel>
          )}
          <AlertDialogAction
            onClick={onConfirm}
            disabled={busy || (isPrompt && required && !value.trim())}
            className={`!rounded-none ${style.confirm}`}
            data-testid="confirm-dialog-confirm"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}
            {finalConfirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function useConfirm() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useConfirm() must be used inside <ConfirmDialogProvider>");
  return ctx;
}
